// Copyright (c) Docugami, Inc. All rights reserved.

import { createSelector } from "@reduxjs/toolkit";
import type { AppState } from "store";
import { ExampleChunk, FeedbackLock, frontendSlice, getFeedbackLockId, selectFeedbackLocksSelector } from "./frontendSlice";

export const exampleChunksDocsetSelector = createSelector([
    (docsetId: string, includeText?: boolean) => docsetId,
    (docsetId: string, includeText?: boolean) => includeText,
],
    (docsetId, includeText) => frontendSlice.endpoints.getExampleChunks.select({ docsetId: docsetId, includeText: includeText })
);

export const createExampleChunksByDocsetLabelSelector = (docsetId: string, label: string, includeText?: boolean) => {
    const lowerLabel = label.toLowerCase();
    const exampleChunksSelector = exampleChunksDocsetSelector(docsetId, includeText);
    return createSelector(
        [
            (state: AppState) => exampleChunksSelector(state)?.data
        ],
        (exampleChunks) => {
            if (!exampleChunks) {
                return [];
            }

            const ids = exampleChunks.ids.filter(x => (x as string).startsWith(`${lowerLabel}|`))
            return ids.map(x => exampleChunks.entities[x]).filter(x => !!x) as ExampleChunk[];
        })
}

export const selectQualityControlWarningsForFile = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string) => docsetId,
        (state: AppState, docsetId: string, fileId: string) => fileId
    ],
    (docsetId, fileId) => frontendSlice.endpoints.getQualityControlWarnings.select({ docsetId: docsetId, fileId: fileId }),
    {
        memoizeOptions: {
            maxSize: 25
        }
    }
)

export const selectLabelState = createSelector(
    [
        (docsetId: string) => docsetId
    ],
    (docsetId) => frontendSlice.endpoints.getLabels.select(docsetId)
)

export const selectFeedbackLock = createSelector(
    [
        (state: AppState, docsetId: string, fileId: string, labelId: string) => selectFeedbackLocksSelector(docsetId)(state),
        (state: AppState, docsetId: string, fileId: string, labelId: string) => getFeedbackLockId(fileId, labelId)
    ],
    (locksState, lockId): FeedbackLock | undefined => locksState.data?.entities[lockId]
)